/**
 * The words for a role that was benchmarked and not priced (STE-1063).
 *
 * Such a role was compared with the survey, and no survey job was close enough
 * to price it from. The matcher usually names the two or three jobs that came
 * closest, with why each one is not this job.
 *
 * **It used to read "no match found" on the list, and its page then listed
 * the jobs that came closest.** A customer reasonably asked which was true. So
 * the words say what happened: not priced, because none was close enough.
 * Never that nothing was found.
 *
 * One wording for every such role. Whether the survey does not cover the work
 * at all, or covers it and nothing fits closely, is said only in the matcher's
 * own sentences, and a label guessed from those would sometimes be wrong.
 * "None close enough" is true of both.
 *
 * Kept in one place so the list, the run results, the page, the PDF and the
 * Disagree card cannot drift apart. The server's status, `no match found`, is
 * unchanged: it is a value programs read, and only the screen's words moved.
 */

import type { Benchmark } from '../types'

export const NOT_PRICED = {
  /** The label on the Benchmarking list and in a run's results. */
  label: 'not priced, none close enough',
  /** The heading at the top of the role's page, and in the PDF. */
  heading: 'Not priced: no survey job is close enough',
  /** Under the heading, only where the jobs that came closest are listed. */
  closest: 'Stello compared this role with your survey. The jobs that came closest are listed below, with why each one is not this job.',
  /** One side of the choice between answers, when that side priced nothing. */
  side: 'Not priced',
  /** The choice, when the re-match still priced nothing. */
  still: 'Still not priced',
} as const

/** The words a status is shown in. Only the not priced one differs from what the server sends. */
export function statusWords(status: string): string {
  return status === 'no match found' ? NOT_PRICED.label : status
}

/** How a finished run counts the roles it did not price. */
export function notPricedCount(n: number): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'was' : 'were'} not priced: no survey job was close enough.`
}

/** The jobs that came closest, each with a code and a reason, in the order the matcher named them. */
export function closestOf(bench: Pick<Benchmark, 'rejected'>): { code: string; reason: string }[] {
  return (bench.rejected ?? []).filter((r) => r.code && r.reason)
}

/** The line under the heading, or null where no closest job is listed to point at. */
export function closestLine(bench: Pick<Benchmark, 'rejected'>): string | null {
  return closestOf(bench).length > 0 ? NOT_PRICED.closest : null
}
